import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Mail,
  Search,
  Save,
  Send,
  ChevronRight,
  CheckCircle2,
  Power,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { EmailAuditLog } from "@/components/notifications/EmailAuditLog";

const CATEGORY_TONE = {
  "Toborzás": "bg-violet-50 text-violet-700 ring-violet-200",
  "Karbantartás": "bg-amber-50 text-amber-700 ring-amber-200",
  "Baleseti jegyzőkönyv": "bg-rose-50 text-rose-700 ring-rose-200",
  "Munkavállaló": "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

export default function NotificationsPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setTemplates(data);
      if (!selectedId && data.length) setSelectedId(data[0]._id);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        (t.title || "").toLowerCase().includes(q) ||
        (t.category || "").toLowerCase().includes(q) ||
        (t.event || "").toLowerCase().includes(q)
    );
  }, [templates, search]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const t of filtered) {
      const k = t.category || "Egyéb";
      if (!groups[k]) groups[k] = [];
      groups[k].push(t);
    }
    return groups;
  }, [filtered]);

  const selected = templates.find((t) => t._id === selectedId);

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="notifications-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Értesítések</h1>
          <p className="mt-1 text-sm text-slate-500">
            E-mail sablonok kezelése és kiküldési napló. A módosított szöveg azonnal érvénybe lép a következő küldéskor.
          </p>
        </div>
        <div className="text-[11px] text-slate-400">
          <span className="font-semibold text-slate-600">{templates.filter((t) => t.enabled).length}</span> aktív
          {" / "}{templates.length} összesen
        </div>
      </div>

      <Tabs defaultValue="templates" className="space-y-5">
        <TabsList data-testid="notifications-tabs">
          <TabsTrigger value="templates" data-testid="tab-templates">
            Sablonok
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            Naplók
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
        {/* Left list */}
        <aside className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés sablonra..."
              className="pl-8"
              data-testid="notifications-search"
            />
          </div>

          {loading ? (
            <div className="rounded-2xl border border-slate-200/70 bg-white p-8 text-center">
              <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : Object.entries(grouped).length === 0 ? (
            <div className="rounded-2xl border border-slate-200/70 bg-white p-8 text-center text-sm text-slate-400">
              Nincs sablon
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <div
                  className={`mb-1.5 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] ring-1 ${CATEGORY_TONE[category] || "bg-slate-50 text-slate-700 ring-slate-200"}`}
                >
                  {category}
                </div>
                <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm">
                  {items.map((t) => (
                    <button
                      key={t._id}
                      onClick={() => setSelectedId(t._id)}
                      data-testid={`notification-item-${t.event}`}
                      className={`flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2.5 text-left last:border-b-0 transition-colors ${
                        selectedId === t._id ? "bg-slate-50" : "hover:bg-slate-50/60"
                      }`}
                    >
                      <Mail className={`h-3.5 w-3.5 ${t.enabled ? "text-slate-700" : "text-slate-300"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm font-medium text-slate-800">{t.title}</div>
                        <div className="truncate text-[11px] text-slate-400">{t.event}</div>
                      </div>
                      {!t.enabled && (
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">ki</span>
                      )}
                      <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </aside>

        {/* Right editor */}
        <main>
          {selected ? (
            <TemplateEditor template={selected} onSaved={load} />
          ) : (
            <div className="rounded-2xl border border-slate-200/70 bg-white p-12 text-center text-sm text-slate-400">
              Válassz egy sablont a szerkesztéshez.
            </div>
          )}
        </main>
      </div>
        </TabsContent>

        <TabsContent value="logs" className="space-y-5">
          <EmailAuditLog templates={templates} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TemplateEditor({ template, onSaved }) {
  const [subject, setSubject] = useState(template.subject || "");
  const [body, setBody] = useState(template.body_html || "");
  const [enabled, setEnabled] = useState(Boolean(template.enabled));
  const [extras, setExtras] = useState(template.extra_recipients || []);
  const [newExtra, setNewExtra] = useState("");
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setSubject(template.subject || "");
    setBody(template.body_html || "");
    setEnabled(Boolean(template.enabled));
    setExtras(template.extra_recipients || []);
    setNewExtra("");
    setTestTo("");
  }, [template._id]); // reload when switching template

  const dirty =
    subject !== (template.subject || "") ||
    body !== (template.body_html || "") ||
    enabled !== Boolean(template.enabled) ||
    JSON.stringify(extras) !== JSON.stringify(template.extra_recipients || []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/notifications/${template._id}`, {
        subject, body_html: body, enabled, extra_recipients: extras,
      });
      toast.success("Sablon mentve.");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const addExtra = () => {
    const e = newExtra.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Érvénytelen e-mail."); return; }
    if (extras.includes(e)) { toast.error("Már szerepel a listán."); return; }
    setExtras([...extras, e]); setNewExtra("");
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Add meg a tesztcím e-mailt."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/notifications/${template._id}/test-send`, {
        to: testTo.trim(),
      });
      if (data?.skipped) {
        toast.error("A Resend nincs konfigurálva (RESEND_API_KEY hiányzik a .env-ben).");
      } else if (data?.error) {
        toast.error(`Resend hiba: ${data.error}`);
      } else {
        toast.success("Teszt levél elküldve.");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Küldés sikertelen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm" data-testid="template-editor">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900">{template.title}</h2>
              {enabled ? (
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> Aktív
                </Badge>
              ) : (
                <Badge variant="secondary">Kikapcsolva</Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-500">{template.description}</p>
            <p className="mt-1 text-[11px] font-mono text-slate-400">{template.event}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Küldés</span>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="template-enabled-switch"
            />
            <Power className={`h-4 w-4 ${enabled ? "text-emerald-600" : "text-slate-300"}`} />
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-600">Tárgy</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="template-subject"
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">Üzenet (HTML)</label>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">
                Mustache-szintaxis: <code>{`{{változó}}`}</code> · <code>{`{{#if var}}...{{/if}}`}</code>
              </span>
            </div>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="font-mono text-[12px] leading-relaxed"
              data-testid="template-body"
            />
          </div>

          {/* Available variables */}
          {(template.available_vars || []).length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Elérhető változók
              </div>
              <div className="flex flex-wrap gap-1.5">
                {template.available_vars.map((v) => (
                  <code
                    key={v}
                    onClick={() => navigator.clipboard?.writeText(`{{${v}}}`)}
                    className="cursor-copy rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-mono text-slate-700 hover:bg-slate-100"
                    title="Kattints a vágólapra másoláshoz"
                  >
                    {`{{${v}}}`}
                  </code>
                ))}
              </div>
            </div>
          )}

          {/* Extra recipients */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-600">
              Mindig CC-zett címek (a rendszer-címzettek mellett)
            </label>
            <div className="flex flex-wrap gap-1.5">
              {extras.map((e, idx) => (
                <Badge key={idx} variant="secondary" className="gap-1 px-2 py-1">
                  {e}
                  <button
                    type="button"
                    onClick={() => setExtras(extras.filter((_, i) => i !== idx))}
                    className="rounded p-0.5 hover:bg-rose-100 hover:text-rose-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <div className="flex items-center gap-1.5">
                <Input
                  value={newExtra}
                  onChange={(e) => setNewExtra(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExtra())}
                  placeholder="email@cim.hu"
                  className="h-7 w-44 text-xs"
                  data-testid="extra-recipient-input"
                />
                <Button type="button" size="sm" variant="ghost" onClick={addExtra}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="teszt cím e-mail"
              className="h-9 w-56 text-xs"
              data-testid="template-test-to"
            />
            <Button
              variant="outline" size="sm"
              onClick={sendTest}
              disabled={sending || !testTo.trim()}
              data-testid="template-test-send"
            >
              {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
              Teszt küldés
            </Button>
          </div>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="template-save"
          >
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Mentés
          </Button>
        </div>
      </div>
    </section>
  );
}
